import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, type RouteObject } from 'react-router';
import { Loading, SignedInLayout } from './components.js';
import { I18nProvider } from './i18n.js';
import { AccountPage, HomePage, SecurityPage } from './pages/AccountPages.js';
import { AuditLogPage } from './pages/AuditLogPage.js';
import { CollectionEditorPage, CollectionsPage } from './pages/catalog/CollectionPages.js';
import { ProductEditorPage } from './pages/catalog/ProductEditorPage.js';
import { ProductsPage } from './pages/catalog/ProductsPage.js';
import { InventoryPage, VariantStockPage } from './pages/inventory/InventoryPages.js';
import { CheckoutSettingsPage } from './pages/checkout/CheckoutSettingsPage.js';
import { DiscountEditorPage, DiscountsPage } from './pages/checkout/DiscountPages.js';
import { AnalyticsPage } from './pages/analytics/AnalyticsPage.js';
import { IntegrationsPage } from './pages/integrations/IntegrationsPage.js';
import { PrivacyPage } from './pages/privacy/PrivacyPage.js';
import { CodSettingsPage } from './pages/cod/CodSettingsPage.js';
import { CarriersPage } from './pages/shipping/CarriersPage.js';
import { PaymentsPage } from './pages/payments/PaymentsPage.js';
import { BillingPage } from './pages/billing/BillingPage.js';
import { DomainsPage } from './pages/billing/DomainsPage.js';
import { InvoiceSettingsPage } from './pages/orders/InvoiceSettingsPage.js';
import { CommentsPage, ReviewPage, ReviewsPage } from './pages/reviews/ReviewsPages.js';
import { OrderPage, OrdersPage } from './pages/orders/OrderPages.js';
import { InvitationPage } from './pages/InvitationPage.js';
import { MediaPage } from './pages/MediaPage.js';
import { ThemePage } from './pages/theme/ThemePage.js';
import { MfaVerifyPage } from './pages/MfaPages.js';
import { SignInPage } from './pages/SignInPage.js';
import { NewStorePage, StorePage } from './pages/StorePages.js';
import { SetupWizardPage } from './pages/onboarding/SetupWizardPage.js';
import { StartPage } from './pages/onboarding/StartPage.js';
import { SessionProvider } from './session.js';

// Enrollment pulls in the QR code encoder: loaded only when needed.
const MfaSetupPage = lazy(async () => ({
  default: (await import('./pages/MfaSetupPage.js')).MfaSetupPage,
}));

export const routes: RouteObject[] = [
  { path: '/sign-in', element: <SignInPage /> },
  { path: '/mfa', element: <MfaVerifyPage /> },
  { path: '/start', element: <StartPage /> },
  {
    element: <SignedInLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'account', element: <AccountPage /> },
      { path: 'security', element: <SecurityPage /> },
      {
        path: 'security/mfa',
        element: (
          <Suspense fallback={<Loading />}>
            <MfaSetupPage />
          </Suspense>
        ),
      },
      { path: 'stores/new', element: <NewStorePage /> },
      { path: 'stores/:storeId', element: <StorePage /> },
      { path: 'stores/:storeId/setup', element: <SetupWizardPage /> },
      { path: 'stores/:storeId/audit', element: <AuditLogPage /> },
      { path: 'stores/:storeId/media', element: <MediaPage /> },
      { path: 'stores/:storeId/products', element: <ProductsPage /> },
      { path: 'stores/:storeId/products/new', element: <ProductEditorPage /> },
      { path: 'stores/:storeId/products/:productId', element: <ProductEditorPage /> },
      { path: 'stores/:storeId/collections', element: <CollectionsPage /> },
      { path: 'stores/:storeId/inventory', element: <InventoryPage /> },
      { path: 'stores/:storeId/theme', element: <ThemePage /> },
      { path: 'stores/:storeId/checkout', element: <CheckoutSettingsPage /> },
      { path: 'stores/:storeId/discounts', element: <DiscountsPage /> },
      { path: 'stores/:storeId/discounts/new', element: <DiscountEditorPage /> },
      { path: 'stores/:storeId/discounts/:discountId', element: <DiscountEditorPage /> },
      { path: 'stores/:storeId/orders', element: <OrdersPage /> },
      { path: 'stores/:storeId/orders/:orderId', element: <OrderPage /> },
      { path: 'stores/:storeId/invoicing', element: <InvoiceSettingsPage /> },
      { path: 'stores/:storeId/integrations', element: <IntegrationsPage /> },
      { path: 'stores/:storeId/analytics', element: <AnalyticsPage /> },
      { path: 'stores/:storeId/privacy', element: <PrivacyPage /> },
      { path: 'stores/:storeId/cod', element: <CodSettingsPage /> },
      { path: 'stores/:storeId/carriers', element: <CarriersPage /> },
      { path: 'stores/:storeId/cash', element: <PaymentsPage /> },
      { path: 'stores/:storeId/billing', element: <BillingPage /> },
      { path: 'stores/:storeId/domains', element: <DomainsPage /> },
      { path: 'stores/:storeId/reviews', element: <ReviewsPage /> },
      { path: 'stores/:storeId/reviews/:reviewId', element: <ReviewPage /> },
      { path: 'stores/:storeId/comments', element: <CommentsPage /> },
      { path: 'stores/:storeId/inventory/:variantId', element: <VariantStockPage /> },
      { path: 'stores/:storeId/collections/new', element: <CollectionEditorPage /> },
      { path: 'stores/:storeId/collections/:collectionId', element: <CollectionEditorPage /> },
      { path: 'invitations/:token', element: <InvitationPage /> },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
];

export function App() {
  const router = createBrowserRouter(routes);
  return (
    <I18nProvider>
      <SessionProvider>
        <RouterProvider router={router} />
      </SessionProvider>
    </I18nProvider>
  );
}
